import { supabase } from '../lib/supabaseClient';
import { compressImage } from '../lib/compressImage';
import { logAction } from './audit';

export type AuthorizationKind = 'tardanza' | 'falta' | 'permiso';
export type AuthorizationStatus = 'pendiente' | 'aprobado' | 'rechazado';

export const AUTHORIZATION_KIND_LABEL: Record<AuthorizationKind, string> = {
  tardanza: 'Tardanza',
  falta: 'Falta',
  permiso: 'Permiso',
};

export const AUTHORIZATION_STATUS_LABEL: Record<AuthorizationStatus, string> = {
  pendiente: 'Pendiente',
  aprobado: 'Aprobada',
  rechazado: 'Rechazada',
};

const COLS =
  'id, worker_id, kind, reason, event_date, evidence_path, status, ' +
  'reviewed_by, review_note, reviewed_at, created_at';

export interface AuthorizationRow {
  id: string;
  worker_id: string;
  kind: AuthorizationKind;
  reason: string;
  event_date: string;
  evidence_path: string | null;
  status: AuthorizationStatus;
  reviewed_by: string | null;
  review_note: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface AuthorizationWithWorker extends AuthorizationRow {
  worker: { id: string; dni: string; first_names: string; last_names: string } | null;
}

export interface CreateAuthorizationInput {
  kind: AuthorizationKind;
  eventDate: string;
  reason: string;
  evidence?: Blob | null;
}

/** Ya hay una solicitud `pendiente` del mismo tipo y fecha (índice único parcial de la tabla). */
export class DuplicatePendingAuthorizationError extends Error {
  constructor(kind: AuthorizationKind, eventDate: string) {
    super(
      `Ya enviaste una solicitud de ${AUTHORIZATION_KIND_LABEL[kind].toLowerCase()} ` +
        `para el ${eventDate.split('-').reverse().join('/')} y sigue pendiente. ` +
        'Espera la respuesta de tu coordinador.',
    );
    this.name = 'DuplicatePendingAuthorizationError';
  }
}

async function currentWorkerId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) throw new Error('Tu sesión terminó. Vuelve a ingresar.');
  return data.user.id;
}

/**
 * Crea una solicitud `pendiente` del trabajador logueado. Si adjunta evidencia,
 * la comprime y la sube a authorizations/<worker_id>/<uuid>.jpg antes de insertar.
 */
export async function createAuthorization(
  input: CreateAuthorizationInput,
): Promise<AuthorizationRow> {
  const workerId = await currentWorkerId();
  const reason = input.reason.trim();
  if (reason === '') throw new Error('Escribe el motivo de tu solicitud.');

  const dup = await supabase
    .from('authorizations')
    .select('id')
    .eq('worker_id', workerId)
    .eq('kind', input.kind)
    .eq('event_date', input.eventDate)
    .eq('status', 'pendiente')
    .limit(1);
  if (dup.error) throw new Error(dup.error.message);
  if ((dup.data ?? []).length > 0) {
    throw new DuplicatePendingAuthorizationError(input.kind, input.eventDate);
  }

  let evidencePath: string | null = null;
  if (input.evidence) {
    const jpg = await compressImage(input.evidence);
    evidencePath = `authorizations/${workerId}/${crypto.randomUUID()}.jpg`;
    const up = await supabase.storage
      .from('photos')
      .upload(evidencePath, jpg, { contentType: 'image/jpeg', upsert: false });
    if (up.error) throw new Error('No se pudo subir la foto: ' + up.error.message);
  }

  const ins = await supabase
    .from('authorizations')
    .insert({
      worker_id: workerId,
      kind: input.kind,
      reason,
      event_date: input.eventDate,
      evidence_path: evidencePath,
    })
    .select(COLS)
    .single();
  if (ins.error) {
    if (ins.error.code === '23505') {
      throw new DuplicatePendingAuthorizationError(input.kind, input.eventDate);
    }
    throw new Error('No se pudo enviar tu solicitud: ' + ins.error.message);
  }

  // `COLS` es una concatenación (tipo `string`, no literal), así que supabase-js no puede
  // inferir la forma de la fila y la tipa como GenericStringError: se pasa por `unknown`,
  // igual que en `listAuthorizations` más abajo.
  const row = ins.data as unknown as AuthorizationRow;
  await logAction('crear_autorizacion', 'authorizations', row.id, {
    kind: row.kind,
    event_date: row.event_date,
    evidence_path: evidencePath,
  });
  return row;
}

/** Solicitudes del trabajador logueado, de la más reciente a la más antigua. */
export async function listMyAuthorizations(): Promise<AuthorizationRow[]> {
  const workerId = await currentWorkerId();
  const { data, error } = await supabase
    .from('authorizations')
    .select(COLS)
    .eq('worker_id', workerId)
    .order('event_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as AuthorizationRow[];
}

export interface AuthorizationFilters {
  status?: AuthorizationStatus | 'todas';
  from?: string;
  to?: string;
}

/** Bandeja del panel (RLS: coordinador y admin ven todas). `status: 'todas'` no filtra. */
export async function listAuthorizations(
  filters: AuthorizationFilters = {},
): Promise<AuthorizationWithWorker[]> {
  let q = supabase
    .from('authorizations')
    .select(
      COLS +
        ', worker:profiles!authorizations_worker_id_fkey(id, dni, first_names, last_names)',
    );
  if (filters.status && filters.status !== 'todas') q = q.eq('status', filters.status);
  if (filters.from) q = q.gte('event_date', filters.from);
  if (filters.to) q = q.lte('event_date', filters.to);
  const { data, error } = await q
    .order('event_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as AuthorizationWithWorker[];
}

/** Aprueba o rechaza vía RPC `review_authorization` (la RPC valida rol y audita). */
export async function reviewAuthorization(
  id: string,
  newStatus: 'aprobado' | 'rechazado',
  note: string,
): Promise<void> {
  const { error } = await supabase.rpc('review_authorization', {
    auth_id: id,
    new_status: newStatus,
    note,
  });
  if (error) throw new Error(error.message);
}

/** URL firmada de 60 segundos para la foto de evidencia (bucket privado `photos`). */
export async function getEvidenceUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from('photos').createSignedUrl(path, 60);
  if (error || !data) throw new Error(error?.message ?? 'No se pudo abrir la evidencia.');
  return data.signedUrl;
}

export interface ApprovedAuthorization {
  id: string;
  worker_id: string;
  kind: AuthorizationKind;
  event_date: string;
  reason: string;
  status: 'aprobado';
  review_note: string | null;
}

/**
 * Autorizaciones APROBADAS de un conjunto de trabajadores dentro de [dateFrom, dateTo].
 * ESTA es la ÚNICA definición del símbolo: sustituye a la de la Task 30 Step 3
 * (misma firma; conserva la degradación a [] cuando la tabla aún no existe).
 * La consumen Validación (Task 30), el cierre (mapea a `AuthorizationRecord` de
 * domain/payroll) y la vista por recurso (Task 39).
 */
export async function listApprovedAuthorizations(
  workerIds: string[],
  dateFrom: string,
  dateTo: string,
): Promise<ApprovedAuthorization[]> {
  if (workerIds.length === 0) return [];
  const { data, error } = await supabase
    .from('authorizations')
    .select('id, worker_id, kind, event_date, reason, status, review_note')
    .in('worker_id', workerIds)
    .eq('status', 'aprobado')
    .gte('event_date', dateFrom)
    .lte('event_date', dateTo)
    .order('event_date', { ascending: true });
  if (error) {
    if (error.code === '42P01') {
      console.warn('Tabla authorizations aún no creada; se omiten las justificaciones.');
      return [];
    }
    throw new Error(error.message);
  }
  return (data ?? []) as ApprovedAuthorization[];
}
